<template>
  <Transition name="promo-modal">
    <div v-if="isOpen" class="promo-modal-overlay" @click.self="close">
      <div class="promo-modal">
        <!-- Language selector -->
        <div class="promo-lang-selector">
          <button v-for="opt in langOptions" :key="opt.code" class="promo-lang-btn"
            :class="{ active: lang === opt.code }" @click="lang = opt.code">{{ opt.label }}</button>
        </div>

        <!-- Close button -->
        <button type="button" class="promo-modal-close" :aria-label="t.close" @click.stop="close">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        <!-- Slideshow -->
        <div class="promo-slideshow">
          <div class="promo-slides" :style="{
            width: `${slides.length * 100}%`,
            transform: `translateX(-${activeSlide * (100 / slides.length)}%)`
          }">
            <div v-for="(slide, idx) in slides" :key="idx" class="promo-slide"
              :style="{ width: `${100 / slides.length}%` }">
              <div class="promo-slide-media"
                :class="{ 'placeholder': !slide.isContacts && !(slide.images && slide.images.length) && !slide.youtubeId }">
                <template v-if="slide.isContacts">
                  <div class="promo-contacts-grid">
                    <a v-for="contact in contacts" :key="contact.name" :href="contact.url" target="_blank"
                      rel="noopener noreferrer" class="promo-contact-card"
                      :title="`Chat with ${contact.name} on WhatsApp`">
                      <img :src="contact.image" :alt="contact.name" class="promo-contact-img" />
                      <span class="promo-contact-name">{{ contact.name }}</span>
                      <svg class="promo-contact-wa" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path fill="currentColor"
                          d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                    </a>
                  </div>
                </template>
                <template v-else-if="slide.youtubeId || (slide.images && slide.images.length)">
                  <div v-if="slide.youtubeId" class="promo-slide-video-wrap">
                    <iframe class="slide-youtube-iframe" :src="`https://www.youtube.com/embed/${slide.youtubeId}?rel=0`"
                      title="YouTube video" frameborder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowfullscreen />
                  </div>
                  <div v-if="slide.images && slide.images.length" class="promo-slide-images"
                    :class="['count-' + slide.images.length, { 'compact': slide.youtubeId }]">
                    <img v-for="(img, imgIdx) in slide.images" :key="imgIdx" :src="img" :alt="slide.title"
                      class="promo-slide-img-clickable" title="Click to view full size" @click="showFullSize(img)" />
                  </div>
                </template>
                <div v-else class="slide-placeholder-content">
                  <span class="placeholder-icon">{{ slide.placeholderIcon || '📹' }}</span>
                </div>
              </div>
              <div class="promo-slide-caption">
                <h3>{{ slide.title }}</h3>
                <p>{{ slide.description }}</p>
              </div>
            </div>
          </div>

          <!-- Navigation dots -->
          <div class="promo-dots">
            <button v-for="(slide, idx) in slides" :key="idx" class="promo-dot" :class="{ active: activeSlide === idx }"
              :aria-label="`Go to slide ${idx + 1}`" @click="activeSlide = idx" />
          </div>

          <!-- Arrow navigation -->
          <button v-if="activeSlide > 0" class="promo-arrow promo-arrow-prev" aria-label="Previous"
            @click="activeSlide--">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button v-if="activeSlide < slides.length - 1" class="promo-arrow promo-arrow-next" aria-label="Next"
            @click="activeSlide++">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>

        <!-- Learn more CTA -->
        <div class="promo-cta">
          <p class="promo-cta-text">{{ t.ctaText }}</p>
          <div class="promo-whatsapp-links">
            <a v-for="contact in contacts" :key="contact.name" :href="contact.url" target="_blank"
              rel="noopener noreferrer" class="promo-whatsapp-btn" :title="`Chat with ${contact.name} on WhatsApp`">
              <img v-if="contact.image" :src="contact.image" :alt="contact.name" class="contact-avatar" />
              <svg class="whatsapp-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill="currentColor"
                  d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              {{ contact.name }}
            </a>
          </div>
        </div>
      </div>

      <!-- Full-size image overlay -->
      <Transition name="promo-lightbox">
        <div v-if="expandedImageSrc" class="promo-image-lightbox" @click.self="showFullSize(null)">
          <button type="button" class="promo-lightbox-close" aria-label="Close" @click.stop="showFullSize(null)">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
          <img :src="expandedImageSrc" alt="Full size" class="promo-lightbox-img" @click.stop="showFullSize(null)" />
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed } from 'vue';
import imgSlide1_1 from './promo_assets/image_slide1_1.png';
import imgSlide1_2 from './promo_assets/image_slide1_2.jpeg';
import imgSlide1_3 from './promo_assets/image_slide1_3.png';
import imgSlide2_1 from './promo_assets/image_slide2_1.png';
import imgSlide2_2 from './promo_assets/image_slide2_2.png';
import imgSlide3_1 from './promo_assets/image_slide3_1.png';
import imgSlide4_1 from './promo_assets/image_slide4_1.png';
import imgSlide5_1 from './promo_assets/image_slide5_1.png';
import imgSlide5_2 from './promo_assets/image_slide5_2.png';
import imgSlide5_3 from './promo_assets/image_slide5_3.png';
import imgSlide5_4 from './promo_assets/image_slide5_4.png';

const props = defineProps({
  active: { type: Boolean, default: false },
  value: { type: Boolean, default: false },
  modelValue: { type: Boolean, default: false },
});

// Same as vs-popup: active.sync. Fallback to value/modelValue for v-model.
const isOpen = computed(function () {
  if (props.active !== undefined && props.active !== null) return props.active;
  if (props.modelValue !== undefined && props.modelValue !== null) return props.modelValue;
  if (props.value !== undefined && props.value !== null) return props.value;
  return false;
});

const emit = defineEmits(['input', 'update:modelValue', 'update:active', 'close']);

const activeSlide = ref(0);
const lang = ref('en');
const expandedImageSrc = ref(null);

const langOptions = [
  { code: 'en', label: 'EN' },
  { code: 'zh', label: '中文' },
  { code: 'ms', label: 'BM' },
  { code: 'ta', label: 'தமிழ்' },
];

const translations = {
  en: {
    close: 'Close',
    ctaText: 'Learn more on pricing and terms',
    slides: [
      { title: 'Protect Your Trucks and Drivers', description: 'Reduce accidents with professional dashcam footage. Stronger evidence for labour court cases, easier insurance claims, and peace of mind.' },
      { title: 'Next-Gen Dashcam', description: 'Professional-grade truck dashcams with crystal-clear recording, wide dynamic range, and rugged build for fleet operations.' },
      { title: 'MDVR with AI Driver Monitoring', description: 'Intelligent Driver Monitoring System detects fatigue, distraction, phone use, and smoking. Keep your drivers and fleet safer.' },
      { title: 'Up to 8 Camera Channels', description: 'Monitor your entire vehicle with up to 8 channels — front, cabin, sides, and rear. Complete visibility for commercial fleets.' },
      { title: 'Real-Time Fleet Visibility', description: 'Live streaming, GPS tracking, and cloud storage. Manage your fleet from anywhere with our integrated platform.' },
      { title: 'Competitive Pricing & Early Bird Offer', description: 'Competitive pricing with early bird discount for select customers. Contact us to find out more.' },
      { title: 'Contact Our Team', description: 'Chat with our customer representatives on WhatsApp.' },
    ],
  },
  zh: {
    close: '关闭',
    ctaText: '了解价格与条款',
    slides: [
      { title: '保护您的卡车与司机', description: '专业行车记录仪减少事故。为劳工法庭案件提供更有力证据，简化保险理赔，安心无忧。' },
      { title: '新一代行车记录仪', description: '专业级卡车行车记录仪，画质清晰，宽动态范围，坚固耐用，适合车队运营。' },
      { title: 'MDVR 智能司机监控', description: '智能司机监控系统可检测疲劳、分心、使用手机和吸烟。让司机和车队更安全。' },
      { title: '最多 8 路摄像', description: '最多 8 路监控整车——前方、驾驶室、侧面和后方。商用车队全方位可视。' },
      { title: '实时车队可见性', description: '实时串流、GPS 追踪和云端存储。通过一体化平台随时随地管理车队。' },
      { title: '优惠价格与早鸟折扣', description: '具有竞争力的价格，精选客户可享早鸟折扣。请联系我们了解更多。' },
      { title: '联系我们的团队', description: '通过 WhatsApp 与我们的客户代表沟通。' },
    ],
  },
  ms: {
    close: 'Tutup',
    ctaText: 'Ketahui harga dan syarat',
    slides: [
      { title: 'Lindungi Lori dan Pemandu Anda', description: 'Kurangkan kemalangan dengan rakaman dashcam profesional. Bukti lebih kukuh untuk kes mahkamah buruh, tuntutan insurans lebih mudah, dan ketenangan fikiran.' },
      { title: 'Dashcam Generasi Baharu', description: 'Dashcam lori gred profesional dengan rakaman jelas, julat dinamik lebar, dan binaan lasak untuk operasi armada.' },
      { title: 'MDVR dengan Pemantauan Pemandu AI', description: 'Sistem Pemantauan Pemandu Pintar mengesan keletihan, gangguan, penggunaan telefon dan merokok. Pastikan pemandu dan armada anda lebih selamat.' },
      { title: 'Sehingga 8 Saluran Kamera', description: 'Pantau keseluruhan kenderaan dengan sehingga 8 saluran — hadapan, kabin, sisi dan belakang. Keterlihatan penuh untuk armada komersial.' },
      { title: 'Keterlihatan Armada Masa Nyata', description: 'Siaran langsung, penjejakan GPS dan penyimpanan awan. Urus armada anda dari mana sahaja dengan platform bersepadu kami.' },
      { title: 'Harga Berdaya Saing & Tawaran Early Bird', description: 'Harga berdaya saing dengan diskaun early bird untuk pelanggan terpilih. Hubungi kami untuk maklumat lanjut.' },
      { title: 'Hubungi Pasukan Kami', description: 'Sembang dengan wakil pelanggan kami di WhatsApp.' },
    ],
  },
  ta: {
    close: 'மூடு',
    ctaText: 'விலை மற்றும் விதிமுறைகள் பற்றி அறிய',
    slides: [
      { title: 'உங்கள் லாரிகள் மற்றும் ஓட்டுநர்களை பாதுகாக்கவும்', description: 'தொழில்முறை டாஷ்காம் பதிவுகளுடன் விபத்துகளை குறைக்கவும். தொழிலாளர் நீதிமன்ற வழக்குகளுக்கு வலுவான ஆதாரம், காப்பீட்டு கோரிக்கைகள் எளிது, மன அமைதி.' },
      { title: 'அடுத்த தலைமுறை டாஷ்காம்', description: 'படிக சுத்தமான பதிவு, பரந்த டைனமிக் ரேஞ்ச் மற்றும் பிளீட் செயல்பாடுகளுக்கான கடினமான கட்டுமானம் கொண்ட தொழில்முறை டிரக் டாஷ்காம்கள்.' },
      { title: 'AI ஓட்டுநர் கண்காணிப்புடன் MDVR', description: 'அறிவார்ந்த ஓட்டுநர் கண்காணிப்பு அமைப்பு சோர்வு, சிதறல், தொலைபேசி பயன்பாடு மற்றும் புகைப்பதை கண்டறியும். உங்கள் ஓட்டுநர்கள் மற்றும் பிளீட்-ஐ பாதுகாப்பாக வைக்கவும்.' },
      { title: '8 கேமரா சேனல்கள் வரை', description: '8 சேனல்கள் வரை — முன், கேபின், பக்கங்கள் மற்றும் பின்புறம் உங்கள் முழு வாகனத்தையும் கண்காணிக்கவும். வணிக பிளீட்-களுக்கு முழு பார்வை.' },
      { title: 'உடனடி பிளீட் பார்வை', description: 'நேரடி ஸ்ட்ரீமிங், GPS கண்காணிப்பு மற்றும் கிளவுட் சேமிப்பு. எங்கள் ஒருங்கிணைந்த தளத்துடன் எங்கிருந்தும் உங்கள் பிளீட்-ஐ நிர்வகிக்கவும்.' },
      { title: 'போட்டி விலை மற்றும் எர்லி பேர்ட் சலுகை', description: 'தேர்ந்தெடுக்கப்பட்ட வாடிக்கையாளர்களுக்கு எர்லி பேர்ட் தள்ளுபடியுடன் போட்டி விலை. மேலும் அறிய எங்களை தொடர்பு கொள்ளுங்கள்.' },
      { title: 'எங்கள் குழுவைத் தொடர்பு கொள்ளுங்கள்', description: 'எங்கள் வாடிக்கையாளர் பிரதிநிதிகளுடன் WhatsApp இல் பேசுங்கள்.' },
    ],
  },
};

const t = computed(function () { return translations[lang.value] || translations.en; });
const slides = computed(function () {
  const s = (translations[lang.value] || translations.en).slides;
  const meta = [
    { placeholderIcon: '🛡', images: [], youtubeId: '-4NhyuvlCaU', isContacts: false },
    { placeholderIcon: '📹', images: [], youtubeId: 'HZrkGd-9D1M', isContacts: false },
    { placeholderIcon: '👁', images: [imgSlide1_1, imgSlide1_3], youtubeId: '', isContacts: false },
    { placeholderIcon: '📷', images: [imgSlide2_1, imgSlide2_2,imgSlide3_1], youtubeId: '', isContacts: false },
    { placeholderIcon: '🌐', images: [imgSlide5_2, imgSlide5_4], youtubeId: '', isContacts: false },
    { placeholderIcon: '💰', images: [,imgSlide4_1,imgSlide5_1, imgSlide5_3], youtubeId: '', isContacts: false },
    { placeholderIcon: '👥', images: [], youtubeId: '', isContacts: true },
  ];
  return s.map(function (slide, i) {
    return Object.assign({}, slide, meta[i]);
  });
});

const contacts = [
  { name: 'Kalai', url: 'https://api.whatsapp.com/send/?phone=%2B60122631184&text&type=phone_number&app_absent=0', image: 'https://igeotechgps.com/wp-content/uploads/2026/02/kalai.jpeg' },
  { name: 'Raja', url: 'https://api.whatsapp.com/send/?phone=%2B60122638498&text&type=phone_number&app_absent=0', image: 'https://igeotechgps.com/wp-content/uploads/2026/02/raja.jpeg' },
  { name: 'Karthik', url: 'https://api.whatsapp.com/send/?phone=%2B60192630350&text&type=phone_number&app_absent=0', image: 'https://igeotechgps.com/wp-content/uploads/2026/02/kathik-e1772084900204.jpeg' },
  { name: 'Hazel', url: 'https://api.whatsapp.com/send/?phone=%2B60182634453&text&type=phone_number&app_absent=0', image: 'https://igeotechgps.com/wp-content/uploads/2026/02/hazel.jpeg' },
];

function showFullSize(src) {
  expandedImageSrc.value = src;
}

function close() {
  expandedImageSrc.value = null;
  emit('update:active', false);
  emit('input', false);
  emit('update:modelValue', false);
  emit('close');
}
</script>

<style scoped>
.promo-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  padding: 1.5rem;
}

.promo-modal {
  position: relative;
  width: 80vw;
  min-width: 80vw;
  height: 80vh;
  min-height: 80vh;
  max-width: 95vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(165deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.08);
}

.promo-lang-selector {
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 1000;
  display: flex;
  gap: 0.25rem;
}

.promo-lang-btn {
  padding: 0.35rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}

.promo-lang-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.promo-lang-btn.active {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.4);
  color: #fff;
}

.promo-modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 1000;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
  pointer-events: auto;
}

.promo-modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.promo-slideshow {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 1rem 1.25rem;
  overflow: hidden;
}

.promo-slides {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.promo-slide {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  box-sizing: border-box;
  height: 100%;
  padding: 0 0.25rem;
}

.promo-slide-media {
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #2d3561 0%, #1a1f3a 100%);
  margin-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem;
}

.promo-slide-video-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 16/10;
  flex-shrink: 0;
  min-height: 0;
}

.slide-youtube-iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.promo-slide-images {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  width: 100%;
  flex: 1;
  min-height: 0;
  justify-content: center;
}

.promo-slide-images img {
  width: 100%;
  flex: 1;
  min-height: 50px;
  max-height: 20vh;
  object-fit: contain;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.2);
}

.promo-slide-images.compact {
  flex-direction: row;
  flex-wrap: nowrap;
}

.promo-slide-images.compact img {
  width: 120px;
  min-width: 80px;
  min-height: 68px;
  max-height: 90px;
}

.promo-slide-media img {
  display: block;
}

/* Customer reps contacts slide */
.promo-contacts-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 1rem;
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 0.5rem;
}
.promo-contact-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  text-decoration: none;
  color: #fff;
  transition: background 0.2s, transform 0.2s;
  border: 1px solid rgba(255, 255, 255, 0.12);
  min-height: 0;
}
.promo-contact-card:hover {
  background: rgba(37, 211, 102, 0.2);
  transform: translateY(-2px);
  color: #fff;
  border-color: rgba(37, 211, 102, 0.4);
}
.promo-contact-img {
  width: min(180px, 26vh);
  height: min(180px, 26vh);
  min-width: 110px;
  min-height: 110px;
  border-radius: 50%;
  object-fit: cover;
}
.promo-contact-name {
  font-size: 1rem;
  font-weight: 600;
  flex-shrink: 0;
}
.promo-contact-wa {
  width: 24px;
  height: 24px;
  color: #25d366;
  flex-shrink: 0;
}

.promo-slide-img-clickable {
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s;
}

.promo-slide-img-clickable:hover {
  opacity: 0.9;
  transform: scale(1.02);
}

/* Full-size image lightbox */
.promo-image-lightbox {
  position: fixed;
  inset: 0;
  z-index: 100001;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.9);
  cursor: zoom-out;
  padding: 2rem;
}

.promo-lightbox-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 100002;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  transition: background 0.2s;
}

.promo-lightbox-close:hover {
  background: rgba(255, 255, 255, 0.25);
}

.promo-lightbox-img {
  max-width: 95vw;
  max-height: 95vh;
  object-fit: contain;
  cursor: zoom-out;
  border-radius: 8px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
}

.promo-lightbox-enter-active,
.promo-lightbox-leave-active {
  transition: opacity 0.2s ease;
}

.promo-lightbox-enter,
.promo-lightbox-leave-to {
  opacity: 0;
}

.promo-lightbox-enter-active .promo-lightbox-img,
.promo-lightbox-leave-active .promo-lightbox-img {
  transition: transform 0.2s ease;
}

.promo-lightbox-enter .promo-lightbox-img,
.promo-lightbox-leave-to .promo-lightbox-img {
  transform: scale(0.95);
}

.promo-slide-media.placeholder {
  aspect-ratio: 16/10;
  min-height: 120px;
}

.promo-slide-media.placeholder .slide-placeholder-content {
  width: 100%;
  height: 100%;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.15) 100%);
}

.placeholder-icon {
  font-size: 4rem;
  opacity: 0.8;
}

.promo-slide-caption {
  text-align: center;
  padding: 0 0.25rem;
  flex-shrink: 0;
}

.promo-slide-caption h3 {
  font-size: 1.15rem;
  font-weight: 600;
  color: #fff;
  margin: 0 0 0.25rem;
  letter-spacing: -0.02em;
}

.promo-slide-caption p {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.35;
  margin: 0;
}

.promo-dots {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex-shrink: 0;
}

.promo-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
}

.promo-dot:hover {
  background: rgba(255, 255, 255, 0.5);
}

.promo-dot.active {
  width: 24px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.95);
}

.promo-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.12);
  border: none;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
}

.promo-arrow:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-50%) scale(1.05);
}

.promo-arrow-prev {
  left: 0.5rem;
}

.promo-arrow-next {
  right: 0.5rem;
}

.promo-cta {
  flex-shrink: 0;
  padding: 0.75rem 1rem;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.promo-cta-text {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 0.5rem;
  text-align: center;
}

.promo-whatsapp-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
}

.promo-whatsapp-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  background: #25d366;
  color: #fff;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 12px;
  transition: background 0.2s, transform 0.2s;
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);
}

.promo-whatsapp-btn:hover {
  background: #20ba5a;
  transform: translateY(-1px);
  color: #fff;
}

.whatsapp-icon {
  width: 20px;
  height: 20px;
}

.contact-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

/* Transitions (Vue 2: enter, enter-active, enter-to, leave, leave-active, leave-to) */
.promo-modal-enter,
.promo-modal-leave-to {
  opacity: 0;
}

.promo-modal-enter-active,
.promo-modal-leave-active {
  transition: opacity 0.3s ease;
}

.promo-modal-enter-active .promo-modal {
  animation: promo-modal-in 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
}

.promo-modal-leave-active .promo-modal {
  animation: promo-modal-out 0.25s ease forwards;
}

@keyframes promo-modal-in {
  from {
    opacity: 0;
    transform: scale(0.92);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes promo-modal-out {
  from {
    opacity: 1;
    transform: scale(1);
  }

  to {
    opacity: 0;
    transform: scale(0.96);
  }
}
</style>
